import React, { useEffect, useRef } from "react";
import "./WhatWeDoPage.scss";
import Container from "../../shared/ui/Container";
import {
  Bug,
  FileText,
  HandFist,
  MicVocal,
  ShieldCheck,
  Wand,
} from "lucide-react";

const WhatWeDoPage = () => {
  const services = [
    {
      id: 1,
      title: "Accessibility Testing",
      Icon: Bug,
      description:
        "We perform a full audit of your product by combining automated testing with manual testing. Only manual testing on real devices can help find all accessibility issues. Our goal is to make sure your product meets internationally recognized standards such as WCAG, ADA, Section 508, ACA, and AODA.",
    },
    {
      id: 2,
      title: "Accessible Design Review",
      Icon: Wand,
      description:
        "We evaluate your design files (Figma, Sketch, or XD) for accessibility before development begins. Our experts review color contrast, text readability, focus states, and content hierarchy to ensure your design meets WCAG standards and provides an inclusive user experience from the very start.",
    },
    {
      id: 3,
      title: "PDF Accessibility Testing",
      Icon: FileText,
      description:
        "Our experts not only test PDFs for accessibility but also remediate all detected issues. We ensure your documents are fully compliant with accessibility standards, readable by assistive technologies, and usable by everyone — regardless of ability.",
    },
    {
      id: 4,
      title: "Training & Consulting",
      Icon: MicVocal,
      description:
        " Accessibility works best when the whole team is involved. Educational programs and consulting services to help your team understand and implement accessibility best practices. We offer training for developers, designers, and testers.",
    },
    {
      id: 5,
      title: "Accessibility Compliance Verification",
      Icon: ShieldCheck,
      description:
        "We help your organization achieve compliance with major accessibility standards, including WCAG 2.2, ADA, Section 508, and AODA. Our team provides detailed accessibility reports, VPAT documentation, and compliance certificates — ensuring your digital products meet legal and ethical requirements.",
    },
    {
      id: 6,
      title: "Ongoing Support",
      Icon: HandFist,
      description:
        "Our collaboration doesn’t end once the project is complete. We’re here whenever you need us — offering continued support, clear communication, and video consultations to help you sustain an accessible and inclusive digital experience for everyone.",
    },
  ];

  const cardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            // Добавляем класс с небольшой задержкой для эффекта цепочки
            setTimeout(() => {
              entry.target.classList.add("visible");
            }, index * 150);
          }
        });
      },
      { threshold: 0.2 }
    );

    cardsRef.current.forEach((card) => observer.observe(card));

    return () => {
      cardsRef.current.forEach((card) => {
        if (card) observer.unobserve(card);
      });
    };
  }, []);

  return (
    <>
      <section
        className="what-we-do-page page-header"
        aria-labelledby="page-title"
      >
        <Container>
          <h1 id="page-title" className="page-header__title">
            What We Do
          </h1>
          <p className="page-header__subtitle">
            We help make digital services easier to use for everyone. Through
            accessibility testing, we find barriers that stop people from using
            websites or apps. Our goal is to make sure everyone can access the
            same information and have equal opportunities.
          </p>
        </Container>
      </section>

      <section className="services-section" aria-labelledby="services-title">
        <div className="services-section__container">
          <h2 id="services-title" className="services-section__title">
            Our Services
          </h2>
          <div className="services-grid">
            {services.map((service, index) => (
              <article
                key={service.id}
                ref={(el) => (cardsRef.current[index] = el)}
                className="service-card"
              >
                <div className="service-card__icon">
                  <service.Icon
                    strokeWidth={1.5}
                    color="#60a5fa"
                    className="service-card__svg"
                  />
                </div>

                <div className="service-card__content">
                  <h3 className="service-card__title">{service.title}</h3>
                  <p className="service-card__description">
                    {service.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="approach" aria-labelledby="approach-title">
        <div className="approach__container">
          <h2 id="approach-title" className="approach__title">
            Our Approach
          </h2>

          <div className="approach__content">
            <p className="approach__text">
              We believe that accessibility is not a checklist — it’s a mindset.
              Our approach combines empathy for users with technical precision,
              ensuring that digital experiences are inclusive, compliant, and
              enjoyable for everyone. <br /> <br />
              Every project begins with understanding your users and their
              needs. We then work collaboratively with your team to implement
              solutions that are both effective and maintainable in the long
              term.
            </p>
            <ul className="approach__list">
              <li>
                We combine automated and manual testing to ensure real
                accessibility.
              </li>
              <li>
                We believe accessibility is a shared responsibility across
                teams.
              </li>
              <li>
                We focus on long-term accessibility, not just passing audits.
              </li>
            </ul>
          </div>
        </div>
      </section>
    </>
  );
};

export default WhatWeDoPage;
